import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import { formatDateOnly, daysUntilDateOnly } from './superadminDateUtils';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import './SuperadminStyles.css';

const ClientesLista = () => {
    const history = useHistory();
    const [clientes, setClientes] = useState([]);
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [showEstadoModal, setShowEstadoModal] = useState(false);
    const [clienteToToggle, setClienteToToggle] = useState(null);
    const [nuevoEstadoPendiente, setNuevoEstadoPendiente] = useState(null);
    const [showModalAcciones, setShowModalAcciones] = useState(false);
    const [clienteAccion, setClienteAccion] = useState(null);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [selectedClientes, setSelectedClientes] = useState([]);
    const [showAccionesMasivas, setShowAccionesMasivas] = useState(false);
    const [accionMasiva, setAccionMasiva] = useState('');
    const itemsPerPage = 20;

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return;
        }
axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchClientes();
    }, [history]);

    useEffect(() => {
        let filtered = clientes;

        // Filtrar por estado - SOLO si se selecciona un estado específico
        if (estadoFilter !== 'todos' && estadoFilter) {
            filtered = filtered.filter(cliente => cliente.estado == estadoFilter);
        }

        // Filtrar por búsqueda
        if (searchTerm) {
            filtered = filtered.filter(cliente =>
                (cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (cliente.nit && cliente.nit.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        setFilteredClientes(ordenarListaAlfabetica(filtered, 'nombre'));
    }, [searchTerm, estadoFilter, clientes]);

    // Solo volver a página 1 al cambiar filtros/búsqueda (no al refrescar tras una acción)
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, estadoFilter]);

    // Si tras refrescar hay menos páginas, no dejar currentPage fuera de rango
    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(filteredClientes.length / itemsPerPage));
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [filteredClientes, currentPage, itemsPerPage]);

    const fetchClientes = async () => {
        try {
            const response = await axios.get('/superadmin/clientes');
            const ordenados = ordenarListaAlfabetica(response.data, 'nombre');
            setClientes(ordenados);
            setFilteredClientes(ordenados);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching clientes:', error);
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('superadmin_token');
                localStorage.removeItem('superadmin_user');
                history.push('/superadmin/login');
            }
            setLoading(false);
        }
    };

    const puedeBloquearCliente = (estado) => [0, 3].includes(parseInt(estado, 10));
    const puedeActivarCliente = (estado) => [1, 2].includes(parseInt(estado, 10));

    const handleCambioEstadoClick = (cliente, nuevoEstado) => {
        setClienteToToggle(cliente);
        setNuevoEstadoPendiente(nuevoEstado);
        setShowEstadoModal(true);
    };

    const handleToggleEstadoConfirm = async () => {
        try {
            await axios.put(`/superadmin/clientes/${clienteToToggle.id}/estado`, {
                estado: nuevoEstadoPendiente,
            });

            setAlert({
                show: true,
                type: 'success',
                message: `Cliente actualizado a estado: ${getEstadoLabel(nuevoEstadoPendiente)}`,
            });
            fetchClientes();
        } catch (error) {
            console.error('Error toggling estado:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al cambiar el estado del cliente',
            });
        } finally {
            setShowEstadoModal(false);
            setClienteToToggle(null);
            setNuevoEstadoPendiente(null);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const isClienteProximoVencer = (fechaVencimiento) => {
        if (!fechaVencimiento) return false;
        const hoy = new Date();
        const vencimiento = new Date(fechaVencimiento);
        const diasRestantes = Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24));
        return diasRestantes <= 7 && diasRestantes >= 0;
    };

    const isClienteLimiteAlcanzado = (cliente) => {
        return cliente.usuarios_actuales >= cliente.limite_usuarios;
    };

    // Helper para obtener la clase del badge según el estado
    const getEstadoBadgeClass = (estado) => {
        switch (parseInt(estado)) {
            case 0: return 'success';      // Activo
            case 1: return 'warning';      // Pendiente pago (bloqueado)
            case 2: return 'danger';       // Retirado
            case 3: return 'info';         // Pendiente pago activo
            default: return 'secondary';
        }
    };

    // Helper para obtener el label del estado
    const getEstadoLabel = (estado) => {
        switch (parseInt(estado)) {
            case 0: return 'Activo';
            case 1: return 'Bloqueado';
            case 2: return 'Retirado';
            case 3: return 'Pend. pago activo';
            default: return 'Sin estado';
        }
    };

    // Selección múltiple
    const handleSelectCliente = (clienteId) => {
        setSelectedClientes(prev => {
            if (prev.includes(clienteId)) {
                return prev.filter(id => id !== clienteId);
            } else {
                return [...prev, clienteId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedClientes.length === currentItems.length) {
            setSelectedClientes([]);
        } else {
            setSelectedClientes(currentItems.map(c => c.id));
        }
    };

    // Acción masiva
    const handleAccionMasiva = async (nuevoEstado) => {
        if (selectedClientes.length === 0) return;

        try {
            setLoading(true);
            await axios.put('/superadmin/clientes/estado-masivo', {
                cliente_ids: selectedClientes,
                estado: nuevoEstado
            });

            setAlert({
                show: true,
                type: 'success',
                message: `${selectedClientes.length} cliente(s) actualizado(s) a: ${getEstadoLabel(nuevoEstado)}`
            });

            setSelectedClientes([]);
            fetchClientes();
        } catch (error) {
            console.error('Error en acción masiva:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al actualizar clientes'
            });
        } finally {
            setLoading(false);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    // Registrar pago y renovar fecha de vencimiento
    const handleRegistrarPago = async () => {
        if (selectedClientes.length === 0) return;

        if (!window.confirm(`¿Desea registrar el pago de ${selectedClientes.length} cliente(s)? Esto activará los clientes y renovará sus fechas de vencimiento según su tipo de contrato.`)) {
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put('/superadmin/clientes/registrar-pago', {
                cliente_ids: selectedClientes
            });

            setAlert({
                show: true,
                type: 'success',
                message: response.data.message
            });

            setSelectedClientes([]);
            fetchClientes();
        } catch (error) {
            console.error('Error registrando pago:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al registrar pago'
            });
        } finally {
            setLoading(false);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    // Registrar pago individual
    const handleRegistrarPagoIndividual = async (cliente) => {
        if (!window.confirm(`¿Desea registrar el pago de ${cliente.nombre}? Esto activará el cliente y renovará su fecha de vencimiento según su tipo de contrato (${cliente.tipo_contrato || 'mensual'}).`)) {
            return;
        }

        try {
            setLoading(true);
            const response = await axios.put('/superadmin/clientes/registrar-pago', {
                cliente_ids: [cliente.id]
            });

            setAlert({
                show: true,
                type: 'success',
                message: response.data.message
            });

            fetchClientes();
        } catch (error) {
            console.error('Error registrando pago:', error);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'Error al registrar pago'
            });
        } finally {
            setLoading(false);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredClientes.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <SuperadminLayout>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4><i className="fas fa-building"></i> Clientes</h4>
                                <Link to="/superadmin/clientes/nuevo" className="btn btn-superadmin">
                                    <i className="fas fa-plus"></i> Crear Cliente
                                </Link>
                            </div>

                            {/* Alertas */}
                            {alert.show && (
                                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                                    {alert.message}
                                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                            )}

                            {/* Filtros */}
                            <div className="row mb-3">
                                <div className="col-md-6">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar por nombre o NIT..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-control"
                                        value={estadoFilter}
                                        onChange={(e) => setEstadoFilter(e.target.value)}
                                    >
                                        <option value="todos">Todos los estados</option>
                                        <option value="0">Activo</option>
                                        <option value="1">Bloqueado</option>
                                        <option value="2">Retirado</option>
                                        <option value="3">Pendiente pago activo</option>
                                    </select>
                                </div>
                            </div>

                            {/* Acciones Masivas */}
                            {selectedClientes.length > 0 && (
                                <div className="alert alert-secondary d-flex justify-content-between align-items-center">
                                    <span>
                                        <i className="fas fa-check-square"></i> {selectedClientes.length} cliente(s) seleccionado(s)
                                    </span>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-success mr-2"
                                            onClick={() => handleAccionMasiva(0)}
                                            title="Activar sin renovar fecha"
                                        >
                                            <i className="fas fa-check"></i> Activar
                                        </button>
                                        <button
                                            className="btn btn-sm btn-dark mr-2"
                                            onClick={handleRegistrarPago}
                                            title="Registrar pago y renovar fecha de vencimiento"
                                        >
                                            <i className="fas fa-dollar-sign"></i> Pagar
                                        </button>
                                        <button
                                            className="btn btn-sm btn-warning mr-2"
                                            onClick={() => handleAccionMasiva(1)}
                                        >
                                            <i className="fas fa-pause"></i> Pend. Pago (bloqueado)
                                        </button>
                                        <button
                                            className="btn btn-sm btn-info mr-2"
                                            onClick={() => handleAccionMasiva(3)}
                                        >
                                            <i className="fas fa-dollar-sign"></i> Pend. Pago Activo
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger mr-2"
                                            onClick={() => handleAccionMasiva(2)}
                                        >
                                            <i className="fas fa-user-slash"></i> Retirado
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={() => setSelectedClientes([])}
                                        >
                                            <i className="fas fa-times"></i> Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Tabla */}
                            <div className="table-responsive">
                                <table className="table table-hover superadmin-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedClientes.length === currentItems.length && currentItems.length > 0}
                                                    onChange={handleSelectAll}
                                                    title="Seleccionar todos"
                                                />
                                            </th>
                                            <th>Nombre</th>
                                            <th>NIT</th>
                                            <th title="Usuarios de sistema activos / límite contratado (el personal sin cupo no suma)">
                                                Usuarios
                                            </th>
                                            <th>Estado</th>
                                            <th>Fecha Creación</th>
                                            <th>Vencimiento</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map(cliente => (
                                                <tr key={cliente.id}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedClientes.includes(cliente.id)}
                                                            onChange={() => handleSelectCliente(cliente.id)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Link to={`/superadmin/clientes/${cliente.id}/informacion`}>
                                                            {cliente.nombre}
                                                        </Link>
                                                        {isClienteLimiteAlcanzado(cliente) && (
                                                            <span className="badge badge-danger ml-2" title="Límite de usuarios activos alcanzado">
                                                                <i className="fas fa-exclamation-circle"></i>
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{cliente.nit}</td>
                                                    <td>
                                                        <span
                                                            className={isClienteLimiteAlcanzado(cliente) ? 'text-danger font-weight-bold' : ''}
                                                            title={`${cliente.usuarios_actuales || 0} usuarios activos de ${cliente.limite_usuarios} contratados`}
                                                        >
                                                            {cliente.usuarios_actuales || 0} / {cliente.limite_usuarios}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-${getEstadoBadgeClass(cliente.estado)}`}>
                                                            {getEstadoLabel(cliente.estado)}
                                                        </span>
                                                    </td>
                                                    <td>{formatDateOnly(cliente.created_at) || new Date(cliente.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        {cliente.fecha_vencimiento ? (
                                                            <span className={isClienteProximoVencer(cliente.fecha_vencimiento) ? 'text-warning font-weight-bold' : ''}>
                                                                {formatDateOnly(cliente.fecha_vencimiento)}
                                                                {isClienteProximoVencer(cliente.fecha_vencimiento) && (
                                                                    <i className="fas fa-exclamation-triangle ml-2" title="Próximo a vencer"></i>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">Sin vencimiento</span>
                                                        )}
                                                    </td>
                                                    <td className="text-nowrap">
                                                        <div className="d-inline-flex align-items-center" style={{ gap: '6px' }}>
                                                            {puedeBloquearCliente(cliente.estado) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-warning"
                                                                    onClick={() => handleCambioEstadoClick(cliente, 1)}
                                                                    title="Bloquear cliente"
                                                                    aria-label="Bloquear cliente"
                                                                >
                                                                    <i className="fas fa-ban"></i>
                                                                </button>
                                                            )}
                                                            {puedeActivarCliente(cliente.estado) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleCambioEstadoClick(cliente, 0)}
                                                                    title="Activar cliente"
                                                                    aria-label="Activar cliente"
                                                                >
                                                                    <i className="fas fa-check"></i>
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                title="Más acciones"
                                                                aria-label="Más acciones"
                                                                onClick={() => {
                                                                    setClienteAccion(cliente);
                                                                    setShowModalAcciones(true);
                                                                }}
                                                            >
                                                                <i className="fas fa-ellipsis-h"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    No se encontraron clientes
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginación */}
                            {totalPages > 1 && (
                                <nav>
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Anterior
                                            </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, index) => (
                                            <li
                                                key={index + 1}
                                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(index + 1)}
                                                >
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Siguiente
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
            <Modal show={showModalAcciones} onHide={() => { setShowModalAcciones(false); setClienteAccion(null); }} centered>
                <Modal.Header closeButton className="py-2">
                    <Modal.Title className="h6 mb-0">Acciones — {clienteAccion?.nombre}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-2">
                    <div className="d-flex flex-column" style={{ gap: '8px' }}>
                        <Link
                            to={`/superadmin/clientes/${clienteAccion?.id}/informacion`}
                            className="btn btn-outline-info text-left"
                            onClick={() => setShowModalAcciones(false)}
                        >
                            <i className="fas fa-info-circle mr-2"></i> Información del cliente
                        </Link>
                        <Link
                            to={`/superadmin/clientes/editar/${clienteAccion?.id}`}
                            className="btn btn-outline-dark text-left"
                            onClick={() => setShowModalAcciones(false)}
                        >
                            <i className="fas fa-edit mr-2"></i> Editar cliente
                        </Link>
                        <Link
                            to={`/superadmin/clientes/${clienteAccion?.id}/usuarios`}
                            className="btn btn-outline-primary text-left"
                            onClick={() => setShowModalAcciones(false)}
                        >
                            <i className="fas fa-users mr-2"></i> Usuarios
                        </Link>
                        <button
                            type="button"
                            className="btn btn-outline-success text-left"
                            onClick={() => {
                                handleRegistrarPagoIndividual(clienteAccion);
                                setShowModalAcciones(false);
                            }}
                        >
                            <i className="fas fa-dollar-sign mr-2"></i> Registrar pago
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showEstadoModal} onHide={() => setShowEstadoModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar cambio de estado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    ¿Está seguro que desea cambiar el estado del cliente{' '}
                    <strong>{clienteToToggle?.nombre}</strong>?
                    <br />
                    <small className="text-muted">
                        Estado actual: <strong>{getEstadoLabel(clienteToToggle?.estado)}</strong>
                        <br />
                        Nuevo estado: <strong>{getEstadoLabel(nuevoEstadoPendiente)}</strong>
                    </small>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEstadoModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant={nuevoEstadoPendiente === 1 ? 'warning' : 'success'}
                        onClick={handleToggleEstadoConfirm}
                    >
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default ClientesLista;
